import React, { useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubscriptionTemplate from './SubscriptionTemplate';
import { displayMessage } from './wsHelpers';
import { connect } from 'react-redux';
import { createWidget, upsertPeerWidget, updateWidget, deleteWidget } from '@/redux/actions';
import { WidgetContext } from '@/WidgetContextProvider';
import { widgetRoomPageId } from '@/enums';

const WS_WIDGETS_BY_HTTP = {
	CREATE_WIDGET: `widgets/POST`,
	UPDATE_WIDGET: `widgets/:widgetID/PATCH`,
	DELETE_WIDGET: `widgets/:widgetID/DELETE`,
};

const WidgetSubscriptions = props => {
	const {
		socket,
		children,
		currentEventId,
		createWidget,
		upsertPeerWidget,
		updateWidget,
		deleteWidget,
	} = props;

	const { resolvePendingWidget } = useContext(WidgetContext);
	const propsToSend = { ...props };
	delete propsToSend.children;

	const { page_id: routePageId } = useParams();
	const pageID = widgetRoomPageId(routePageId);

	// Held in a ref because SubscriptionTemplate subscribes on mount and again on reconnect through
	// first-render closures, and a stale room key would leave us listening to another page.
	const subscribedRoomRef = useRef({ eventID: currentEventId, pageID });

	const subscribeToWidget = () => {
		socket.emit('event/widgets/:eventID/SUBSCRIBE', subscribedRoomRef.current);
	};

	const unsubscribeToWidget = () => {
		socket.emit('event/widgets/:eventID/UNSUBSCRIBE', subscribedRoomRef.current);
	};

	useEffect(() => {
		const subscribed = subscribedRoomRef.current;
		if (subscribed.eventID === currentEventId && subscribed.pageID === pageID) return;
		unsubscribeToWidget();
		subscribedRoomRef.current = { eventID: currentEventId, pageID };
		subscribeToWidget();
	}, [pageID, currentEventId]);

	const listenForCreatedWidget = () => {
		socket.on(WS_WIDGETS_BY_HTTP.CREATE_WIDGET, payload => {
			const { data, error, isAck, clientId } = payload;
			if (error) {
				displayMessage(`widget could not be created`, error);
				if (isAck) resolvePendingWidget(clientId, { failed: true });
				return;
			}
			const { createWidget: widget } = data;
			if (isAck) {
				createWidget(widget, clientId, 'WidgetSubscriptions.created');
				resolvePendingWidget(clientId);
			} else upsertPeerWidget(widget, 'WidgetSubscriptions.peerCreated');
		});
	};

	const listenForUpdatedWidget = () => {
		socket.on(WS_WIDGETS_BY_HTTP.UPDATE_WIDGET, payload => {
			const { data, error, isAck, widgetID } = payload;
			if (error) {
				displayMessage(`widget could not be updated`, error);
				if (isAck) resolvePendingWidget(widgetID, { failed: true });
				return;
			}
			updateWidget(data, 'WidgetSubscriptions.updated');
			if (isAck) resolvePendingWidget(widgetID);
		});
	};

	const listenForDeletedWidget = () => {
		socket.on(WS_WIDGETS_BY_HTTP.DELETE_WIDGET, payload => {
			const { data, error, isAck, widgetID } = payload;
			if (error) {
				displayMessage(`widget could not be deleted`, error);
				if (isAck) resolvePendingWidget(widgetID, { failed: true });
				return;
			}
			const { deleteWidget: widget } = data;
			deleteWidget(widget.id, 'WidgetSubscriptions.deleted');
			if (isAck) resolvePendingWidget(widgetID);
		});
	};

	const listeners = () => [listenForCreatedWidget, listenForUpdatedWidget, listenForDeletedWidget];

	return (
		<>
			<SubscriptionTemplate
				{...props}
				sub={subscribeToWidget}
				unsub={unsubscribeToWidget}
				listeners={listeners()}
				routes={Object.values(WS_WIDGETS_BY_HTTP)}
				from="WidgetSubscriptions"
			/>
			{React.cloneElement(children, propsToSend)}
		</>
	);
};

const msp = ({ ws, auth, events }) => ({
	socket: ws.socket,
	auth0Token: auth.auth0Token,
	currentEventId: events.currentEvent.id,
});

const mdp = dispatch => ({
	createWidget: (widget, clientId, from) => dispatch(createWidget(widget, clientId, from)),
	upsertPeerWidget: (widget, from) => dispatch(upsertPeerWidget(widget, from)),
	updateWidget: (widget, from) => dispatch(updateWidget(widget, from)),
	deleteWidget: (widgetID, from) => dispatch(deleteWidget(widgetID, from)),
});

export default connect(msp, mdp)(WidgetSubscriptions);
